// components/LayerCanvas.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SimpleLoader from './ui/SimpleLoader';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import LayerResizeHandle from './LayerResizeHandle';
import LayerSelectionFrame from './LayerSelectionFrame';
import NativeLayerCutOverlay from './NativeLayerCutOverlay';
import LayerFeedbackOverlays from './LayerFeedbackOverlays';
import { generationAreaCursor, scissorsCursor, getMaskPenCursor } from '#lib/svg';
import {
  buildInteractiveSvgLayerMarkup,
  getSvgLayerPayload,
  getSvgPaintTargetIdFromEventTarget,
  isLayerVectorized,
} from '#lib/svg-layer';
import { getDrawingAwareSvgLayerSource, getDrawingLayerPayload } from '#lib/drawing-layer';
import { toFiniteTimelineMs } from '#lib/workspace-timeline';
import {
  buildStudioLayerContentCssTransform,
  resolveStudioLayerTransform,
} from '#lib/studio-layer-transform';
import { resolveRasterTiles, TILE_ABOVE_SIDE_PX } from '#lib/studio-layer-tiles';
import { HEATMAP_OVERLAY_ALPHA } from '#lib/studio-layer-webgl-raster';
import { resolveLayerWorldPerPixel } from '#lib/studio-layer-density';
import LayerWebGLRaster from './LayerWebGLRaster';
import { getLayerWorkspaceId } from '#types/types';

const VIDEO_END_FRAME_SEEK_OFFSET_SEC = 1 / 30;
export const MAX_PREVIEW_RASTER_DIMENSION = 1600;

const resolvePreviewRasterSize = (width: number, height: number): { width: number; height: number } => {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return { width: 1, height: 1 };
    }
    const longestSide = Math.max(width, height);
    if (longestSide <= MAX_PREVIEW_RASTER_DIMENSION) {
        return { width: Math.round(width), height: Math.round(height) };
    }
    const scale = MAX_PREVIEW_RASTER_DIMENSION / longestSide;
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
};

// The ghost only carries UI flashes (pulse, scissors hover). Power-of-two divisors keep the resize an exact decimation.
const GHOST_MAX_RASTER_PIXELS = 8_000_000;
const resolveGhostRasterDivisor = (width: number, height: number): number => {
    let divisor = 2;
    while ((width / divisor) * (height / divisor) > GHOST_MAX_RASTER_PIXELS) divisor *= 2;
    return divisor;
};

const TILE_CANVAS_STYLE_BASE: React.CSSProperties = { position: 'absolute', imageRendering: 'pixelated' };

const preventDragStart = (event: React.DragEvent<HTMLElement>) => event.preventDefault();

const NO_SOURCE_CONTAINER_STYLE: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};
const NO_SOURCE_TEXT_STYLE: React.CSSProperties = { color: '#888' };
const VIDEO_CONTAINER_STYLE: React.CSSProperties = { width: '100%', height: '100%', position: 'relative' };
const CENTERED_OVERLAY_STYLE: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};
const VIDEO_ERROR_OVERLAY_STYLE: React.CSSProperties = {
    ...CENTERED_OVERLAY_STYLE,
    color: '#ff8a8a',
    fontSize: '12px',
};
const NO_SVG_CONTAINER_STYLE: React.CSSProperties = {
    ...NO_SOURCE_CONTAINER_STYLE,
    userSelect: 'none',
    WebkitUserSelect: 'none',
};
const NO_SVG_TEXT_STYLE: React.CSSProperties = {
    ...NO_SOURCE_TEXT_STYLE,
    userSelect: 'none',
    WebkitUserSelect: 'none',
};
const INLINE_SVG_CONTAINER_STYLE: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    pointerEvents: 'auto',
    userSelect: 'none',
    WebkitUserSelect: 'none',
};
const SVG_IMAGE_STYLE: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'fill',
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
};
const CANVAS_STRETCHED_STYLE: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    imageRendering: 'pixelated',
};

interface Layer2DVideoProps {
    layerId: string;
}

const Layer2DVideo: React.FC<Layer2DVideoProps> = React.memo(({ layerId }) => {
    const { kernel } = useKernel();
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const videoSrc = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.videoSrc);
    const layerLoadError = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.loadError === true);
    const videoBlob = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.videoBlob ?? null);
    const [resolvedVideoSrc, setResolvedVideoSrc] = useState<string | undefined>(undefined);

    // Play ONLY from the kernel-cached blob (a local blob: URL): the asset is never re-fetched
    // over the network and seeks are instant. The objectURL lifecycle is scoped to this element
    // (revoked on blob change / unmount) — the only place a video objectURL is created, so there
    // is no leak and no manual kernel-side revocation.
    //
    // FAIL FAST: there is deliberately NO raw-URL fallback. While the hydrator fetches the blob
    // the element has no src (the loader shows); if that fetch fails the layer surfaces loadError
    // and we show the error state. We never silently stream the raw URL — that would re-download
    // the asset, fire spurious load errors on the raw -> blob swap, and mask the real failure.
    useEffect(() => {
        if (!videoBlob) {
            setResolvedVideoSrc(undefined);
            return undefined;
        }
        const objectUrl = URL.createObjectURL(videoBlob);
        setResolvedVideoSrc(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [videoBlob]);

    // Stable ref callback: an inline one is a new function every render, which makes React
    // detach (call with null) and reattach the element on each state change of this component.
    const registerVideoRef = useCallback((el: HTMLVideoElement | null) => {
        const videoRefs = kernel.current?.videoRefs;
        if (!videoRefs) return;
        videoRefs.current[layerId] = el;
    }, [kernel, layerId]);

    const videoStyle = useMemo((): React.CSSProperties => ({
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        backgroundColor: 'transparent', // Match image layers: an unpainted frame stays invisible, not a gray block
        opacity: isReady ? 1 : 0, // Hide video until it's ready
        transition: 'opacity 0.2s ease-in-out',
    }), [isReady]);

    const handleLoadedMetadata = useCallback(() => {
        setIsReady(true);
        setError(null);
        kernel.current?.setLayer(layerId, existing => ({ ...existing, loadError: false }));
        if (videoSrc) {
            kernel.current?.reportAssetLoaded(videoSrc);
        }
    }, [kernel, layerId, videoSrc]);

    const handleError = useCallback(() => {
        setError('Failed to load video');
        setIsReady(false);
        kernel.current?.setLayer(layerId, existing => ({ ...existing, loadError: true }));
        console.error(`Video Error: Failed to load video from src: ${videoSrc}`);
        if (videoSrc) {
            kernel.current?.reportAssetLoaded(videoSrc);
        }
    }, [videoSrc, kernel, layerId]);

    const handleEnded = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const videoElement = event.currentTarget;
        if (videoElement.loop) return;
        if (videoElement.dataset.faterTimelineControlled === 'true') return;
        const durationSec = videoElement.duration;
        if (!Number.isFinite(durationSec) || durationSec <= 0) return;
        const targetTimeSec = Math.max(0, durationSec - VIDEO_END_FRAME_SEEK_OFFSET_SEC);
        if (Math.abs(videoElement.currentTime - targetTimeSec) < 0.005) return;
        try {
            videoElement.currentTime = targetTimeSec;
        } catch {
            // Best effort: some browsers reject near-end seeks while finalizing playback.
        }
    }, []);

    // Reset local playback state whenever source changes or a manual reload clears loadError.
    useEffect(() => {
        if (!videoSrc) return;
        if (layerLoadError) return;
        setIsReady(false);
        setError(null);
    }, [videoSrc, layerLoadError]);

    if (!videoSrc) {
        // Render a placeholder or nothing if there's no video source
        return (
            <div style={NO_SOURCE_CONTAINER_STYLE}>
                <span style={NO_SOURCE_TEXT_STYLE}>No Video Source</span>
            </div>
        );
    }

    return (
        <div
            style={VIDEO_CONTAINER_STYLE}
        >
            <video
                key={`${videoSrc ?? 'no-src'}|cors-anon-v1`}
                ref={registerVideoRef}
                src={resolvedVideoSrc}
                crossOrigin="anonymous"
                preload="auto"
                style={videoStyle}
                loop={false}
                muted // Muted is crucial for autoplay to work in most browsers
                playsInline // Important for mobile browsers
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                onError={handleError}
            />

            {/* Loading State: only while the hydrator is still fetching the blob (no failure). */}
            {!isReady && !error && !layerLoadError && (
                <div style={CENTERED_OVERLAY_STYLE}>
                    <SimpleLoader />
                </div>
            )}

            {/* Error State: a local <video> error OR a hydrator fetch failure (fail fast). */}
            {(error || layerLoadError) && (
                <div style={VIDEO_ERROR_OVERLAY_STYLE}>
                    {error ?? 'Failed to load video'}
                </div>
            )}

        </div>
    );
});

Layer2DVideo.displayName = 'Layer2DVideo';


interface Layer2DSvgProps {
    layerId: string;
}

const Layer2DSvg: React.FC<Layer2DSvgProps> = React.memo(({ layerId }) => {
    const { kernel } = useKernel();
    const imageSrc = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageSrc);
    const params = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.params);
    const layerLoadError = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.loadError === true);
    const inlineSvgMarkup = useMemo(() => getSvgLayerPayload(params)?.svg ?? null, [params]);
    const interactiveSvgMarkup = useMemo(
        () => buildInteractiveSvgLayerMarkup(inlineSvgMarkup),
        [inlineSvgMarkup]
    );
    const source = useMemo(() => (
      getDrawingAwareSvgLayerSource({ imageSrc: imageSrc ?? undefined, params: params ?? undefined })
    ), [imageSrc, params]);

    const handleLoad = useCallback(() => {
        kernel.current?.setLayer(layerId, existing => ({ ...existing, loadError: false }));
        if (source && !source.startsWith('data:')) {
            kernel.current?.reportAssetLoaded(source);
        }
    }, [kernel, layerId, source]);

    const handleError = useCallback(() => {
        kernel.current?.setLayer(layerId, existing => ({ ...existing, loadError: true }));
        if (source && !source.startsWith('data:')) {
            kernel.current?.reportAssetLoaded(source);
        }
    }, [kernel, layerId, source]);

    const handleSvgMouseDownCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const targetId = getSvgPaintTargetIdFromEventTarget(event.target);
        if (!targetId) return;
        const currentTargetId = kernel.current?.readEntityRuntimeState('StudioLayer', layerId).selectedSvgPaintTargetId;
        if (currentTargetId === targetId) return;
        kernel.current?.patchEntityRuntimeState('StudioLayer', layerId, {
            selectedSvgPaintTargetId: targetId,
        });
        kernel.current?.logUserAction('SVG Paint Target Selected', {
            layer_id: layerId,
            target_id: targetId,
        });
    }, [kernel, layerId]);

    useEffect(() => {
        if (!interactiveSvgMarkup) return;
        kernel.current?.setLayer(layerId, existing => ({ ...existing, loadError: false }));
        if (source && !source.startsWith('data:')) {
            kernel.current?.reportAssetLoaded(source);
        }
    }, [interactiveSvgMarkup, kernel, layerId, source]);

    if (!source) {
        return (
            <div style={NO_SVG_CONTAINER_STYLE}>
                <span style={NO_SVG_TEXT_STYLE}>No SVG Source</span>
            </div>
        );
    }

    if (interactiveSvgMarkup) {
        return (
            <div
                key={`${layerId}|inline-svg-v1`}
                onMouseDownCapture={handleSvgMouseDownCapture}
                onDragStart={preventDragStart}
                style={INLINE_SVG_CONTAINER_STYLE}
                dangerouslySetInnerHTML={{ __html: interactiveSvgMarkup }}
            />
        );
    }

    return (
        <img
            key={`${source}|${layerLoadError ? 'err' : 'ok'}|svg-v1`}
            src={source}
            alt=""
            draggable={false}
            onDragStart={preventDragStart}
            onLoad={handleLoad}
            onError={handleError}
            style={SVG_IMAGE_STYLE}
        />
    );
});

Layer2DSvg.displayName = 'Layer2DSvg';



interface Layer2DCanvasProps {
    layerId: string;
    workspaceId: string;
    heatmapHue: number | null;
    preview?: boolean;
    ghostResolution?: boolean;
}

// A layer always displays at its native resolution, at every zoom: fix performance by how pixels are drawn, never by dropping them.
const Layer2DCanvasComponent: React.FC<Layer2DCanvasProps> = ({ layerId, workspaceId, heatmapHue, preview = false, ghostResolution = false }) => {
    const imageData = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageData);
    const contentVersion = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.contentVersion);
    const rectW = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.w);
    const rectH = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.h);
    // The WebGL raster maps the bitmap unrotated and unmirrored onto its on-screen bounding rect: a transformed layer keeps the tiles.
    const hasUntransformedContent = useKernelStore((state) => (
        buildStudioLayerContentCssTransform(resolveStudioLayerTransform(state.inventory.StudioLayer.registry[layerId]?.params)) === undefined
    ));
    const [isWebGLUnavailable, setIsWebGLUnavailable] = useState(false);
    const markWebGLUnavailable = useCallback(() => setIsWebGLUnavailable(true), []);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const tileCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

    useEffect(() => () => {
        for (const canvas of [canvasRef.current, ...tileCanvasRefs.current]) {
            if (!canvas) continue;
            canvas.width = 1;
            canvas.height = 1;
        }
    }, []);

    const renderedVersionRef = useRef<string | number | null | undefined>(undefined);
    const renderedHueRef = useRef<number | null>(null);
    const renderedWidthRef = useRef<number | null>(null);
    const renderedHeightRef = useRef<number | null>(null);
    const imageDataWidth = imageData?.width;
    const imageDataHeight = imageData?.height;
    const previewRasterSize = useMemo(
        () => resolvePreviewRasterSize(imageDataWidth ?? 0, imageDataHeight ?? 0),
        [imageDataHeight, imageDataWidth]
    );
    const isGhost = ghostResolution && !preview;
    const ghostDivisor = isGhost && imageDataWidth && imageDataHeight ? resolveGhostRasterDivisor(imageDataWidth, imageDataHeight) : 1;
    const renderedWidth = preview
        ? previewRasterSize.width
        : imageDataWidth && Math.ceil(imageDataWidth / ghostDivisor);
    const renderedHeight = preview
        ? previewRasterSize.height
        : imageDataHeight && Math.ceil(imageDataHeight / ghostDivisor);
    const rasterTiles = useMemo(
        () => renderedWidth && renderedHeight && rectW && rectH
            && renderedWidth === imageDataWidth && renderedHeight === imageDataHeight
            && Math.max(renderedWidth, renderedHeight) > TILE_ABOVE_SIDE_PX
            ? resolveRasterTiles(renderedWidth, renderedHeight)
            : null,
        [renderedWidth, renderedHeight, imageDataWidth, imageDataHeight, rectW, rectH]
    );
    const drawsWithWebGL = rasterTiles !== null && hasUntransformedContent && !isWebGLUnavailable;

    useEffect(() => {
        if (drawsWithWebGL) {
            // The tiles are unmounted: forget what they held, so that falling back to them redraws them.
            renderedWidthRef.current = null;
            return;
        }
        if (!imageData || !renderedWidth || !renderedHeight) {
            return;
        }

        if (
            contentVersion === renderedVersionRef.current &&
            heatmapHue === renderedHueRef.current &&
            renderedWidth === renderedWidthRef.current &&
            renderedHeight === renderedHeightRef.current
        ) {
            return;
        }

        const applyHeatmapTo = (target: HTMLCanvasElement, targetCtx: CanvasRenderingContext2D) => {
            if (heatmapHue !== null && typeof heatmapHue === 'number' && Number.isFinite(heatmapHue)) {
                targetCtx.save();
                targetCtx.globalCompositeOperation = 'source-atop';
                targetCtx.fillStyle = `hsla(${heatmapHue}, 100%, 50%, ${HEATMAP_OVERLAY_ALPHA})`;
                targetCtx.fillRect(0, 0, target.width, target.height);
                targetCtx.restore();
            }
        };
        const markRendered = () => {
            renderedVersionRef.current = contentVersion;
            renderedHueRef.current = heatmapHue;
            renderedWidthRef.current = renderedWidth;
            renderedHeightRef.current = renderedHeight;
        };

        if (rasterTiles) {
            let allTilesDrawn = true;
            rasterTiles.forEach((tile, index) => {
                const tileCanvas = tileCanvasRefs.current[index];
                const tileCtx = tileCanvas?.getContext('2d');
                if (!tileCanvas || !tileCtx) {
                    allTilesDrawn = false;
                    return;
                }
                tileCanvas.width = tile.w;
                tileCanvas.height = tile.h;
                tileCtx.putImageData(imageData, -tile.x, -tile.y, tile.x, tile.y, tile.w, tile.h);
                applyHeatmapTo(tileCanvas, tileCtx);
            });
            if (allTilesDrawn) markRendered();
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        canvas.width = renderedWidth;
        canvas.height = renderedHeight;

        const applyHeatmap = () => applyHeatmapTo(canvas, ctx);

        if (isGhost && (renderedWidth !== imageData.width || renderedHeight !== imageData.height)) {
            // Resized natively off the main thread: a JS loop over hundreds of 4K layers stalls the scene on load.
            // Marked rendered only once drawn, so a cancelled draw is redone on the next run instead of left blank.
            let cancelled = false;
            void createImageBitmap(imageData, { resizeWidth: renderedWidth, resizeHeight: renderedHeight, resizeQuality: 'low' })
                .then((bitmap) => {
                    if (!cancelled) {
                        ctx.drawImage(bitmap, 0, 0);
                        applyHeatmap();
                        markRendered();
                    }
                    bitmap.close();
                });
            return () => { cancelled = true; };
        }

        if (renderedWidth === imageData.width && renderedHeight === imageData.height) {
            ctx.putImageData(imageData, 0, 0);
        } else {
            const scaledImageData = ctx.createImageData(renderedWidth, renderedHeight);
            const sourcePixels = imageData.data;
            const targetPixels = scaledImageData.data;
            for (let y = 0; y < renderedHeight; y += 1) {
                const sourceY = Math.min(imageData.height - 1, Math.floor((y * imageData.height) / renderedHeight));
                const sourceRowOffset = sourceY * imageData.width * 4;
                const targetRowOffset = y * renderedWidth * 4;
                for (let x = 0; x < renderedWidth; x += 1) {
                    const sourceOffset = sourceRowOffset + Math.min(imageData.width - 1, Math.floor((x * imageData.width) / renderedWidth)) * 4;
                    const targetOffset = targetRowOffset + x * 4;
                    targetPixels[targetOffset] = sourcePixels[sourceOffset] ?? 0;
                    targetPixels[targetOffset + 1] = sourcePixels[sourceOffset + 1] ?? 0;
                    targetPixels[targetOffset + 2] = sourcePixels[sourceOffset + 2] ?? 0;
                    targetPixels[targetOffset + 3] = sourcePixels[sourceOffset + 3] ?? 0;
                }
            }
            ctx.putImageData(scaledImageData, 0, 0);
        }

        applyHeatmap();
        markRendered();
    }, [contentVersion, drawsWithWebGL, heatmapHue, imageData, isGhost, rasterTiles, renderedHeight, renderedWidth]);

    // Keep the canvas element at its intrinsic CSS pixel size and scale it down/up
    // with a transform. Using width/height: 100% made the browser layout tiny
    // rasters as ~5 CSS px before the viewport zoom, which clipped/misphased the
    // visible bitmap even though the layer hitbox and ImageData were correct.
    const displayScaleX =
        typeof rectW === 'number' && rectW > 0 && typeof renderedWidth === 'number' && renderedWidth > 0
            ? rectW / renderedWidth
            : null;
    const displayScaleY =
        typeof rectH === 'number' && rectH > 0 && typeof renderedHeight === 'number' && renderedHeight > 0
            ? rectH / renderedHeight
            : null;
    const useIntrinsicCanvasLayout = displayScaleX !== null && displayScaleY !== null;

    const canvasStyle = useMemo((): React.CSSProperties => (
        useIntrinsicCanvasLayout
            ? {
                display: 'block',
                width: `${renderedWidth}px`,
                height: `${renderedHeight}px`,
                transform: `scale(${displayScaleX}, ${displayScaleY})`,
                transformOrigin: '0 0',
                imageRendering: 'pixelated',
            }
            : CANVAS_STRETCHED_STYLE
    ), [useIntrinsicCanvasLayout, renderedWidth, renderedHeight, displayScaleX, displayScaleY]);

    if (rasterTiles) {
        return (
            <div style={{ ...canvasStyle, position: 'relative' }} onDragStart={preventDragStart}>
                {drawsWithWebGL
                    ? <LayerWebGLRaster layerId={layerId} workspaceId={workspaceId} heatmapHue={heatmapHue} onUnavailable={markWebGLUnavailable} />
                    : rasterTiles.map((tile, index) => (
                        <canvas
                            key={`${tile.x}:${tile.y}`}
                            ref={(element) => { tileCanvasRefs.current[index] = element; }}
                            style={{ ...TILE_CANVAS_STYLE_BASE, left: tile.x, top: tile.y, width: tile.w, height: tile.h }}
                        />
                    ))}
            </div>
        );
    }

    return (
        <canvas
            ref={canvasRef}
            style={canvasStyle}
        />
    );
};

export const Layer2DCanvas = React.memo(
    Layer2DCanvasComponent,
    (prevProps, nextProps) => {
        return (
            prevProps.layerId === nextProps.layerId &&
            prevProps.workspaceId === nextProps.workspaceId &&
            prevProps.heatmapHue === nextProps.heatmapHue &&
            prevProps.preview === nextProps.preview &&
            prevProps.ghostResolution === nextProps.ghostResolution
        );
    }
);
Layer2DCanvas.displayName = 'Layer2DCanvas';

// ----------------------------------------
// Outer Component: Handles Positioning, Clipping, Events (Also Memoized)
// ----------------------------------------
interface LayerCanvasProps {
    layerId: string;
    isWorkspaceVisible: boolean;
    handleLayerMouseDown: ((event: React.MouseEvent<HTMLDivElement>, layerid: string) => void) | null;
    onEditDrawingTextLayer?: (layerId: string) => boolean;
    isWorkspaceLocked: boolean;
    workspaceId: string;
    handleResizeHandleMouseDown: (event: React.MouseEvent, layerId: string, corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' | 'left' | 'right') => void;
    resizeHandlesPortalHostRef: React.RefObject<HTMLDivElement | null>;
    suppressForComposedPlayback?: boolean;
    preview?: boolean;
}

const RESIZE_HANDLE_CORNERS: ('topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'right' | 'bottom' | 'left')[] = [
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'top',
  'right',
  'bottom',
  'left',
];

// Wrap the outer component with React.memo (using default shallow comparison)
const LayerCanvas: React.FC<LayerCanvasProps> = React.memo(({
    layerId,
    isWorkspaceVisible,
    handleLayerMouseDown,
    onEditDrawingTextLayer,
    isWorkspaceLocked,
    workspaceId,
    handleResizeHandleMouseDown,
    resizeHandlesPortalHostRef,
    suppressForComposedPlayback = false,
    preview = false,
}) => {
    const { kernel } = useKernel();
    const isLayerScopedToWorkspace = useKernelStore((state) => {
      const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
      const layer = state.inventory.StudioLayer.registry[layerId];
      const projectId = typeof workspace?.projectId === 'string' ? workspace.projectId.trim() : '';
      return Boolean(
        workspace &&
        layer &&
        projectId &&
        layer.projectId.trim() === projectId &&
        getLayerWorkspaceId(layer) === workspaceId
      );
    });
    const showResolutionHeatmap = useKernelStore(state => state.showResolutionHeatmap);
    const activeTool = useKernelStore(state => state.activeTool);
    const maskPenMode = useKernelStore(state => state.maskPenMode);
    const isAnyNativeCutActive = useKernelStore((state) => state.nativeLayerCutSession !== null);
    const isScissorsWaitingForTarget = activeTool === 'scissors' && !isAnyNativeCutActive;
    const isNativeCutTarget = useKernelStore((state) => {
      const session = state.nativeLayerCutSession;
      return session?.layerId === layerId && session.workspaceId === workspaceId;
    });
    const shouldHideLayerForPeek = useKernelStore((state) => {
      const session = state.nativeLayerCutSession;
      return state.isAltKeyPressed === true && session?.layerId === layerId && session.workspaceId === workspaceId;
    });
    const shouldHideLayerForParentPreview = useKernelStore((state) => {
      if (state.isAltKeyPressed !== true) return false;
      if (state.nativeLayerCutSession !== null) return false;

      const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
      if (!workspace) return false;

      const projectId = typeof workspace.projectId === 'string' ? workspace.projectId.trim() : '';
      if (!projectId) return false;

      const runtime = state.inventory.Project.registry[projectId]?.runtime;
      const runtimeActiveWorkspaceId =
        typeof runtime?.UIWorkspaceId === 'string' ? runtime.UIWorkspaceId.trim() : '';
      if (!runtimeActiveWorkspaceId || runtimeActiveWorkspaceId !== workspaceId) return false;

      const versions = Array.isArray(workspace.versions) ? workspace.versions : [];
      if (versions.length === 0) return false;

      const activeVersionId =
        (typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0)
          ? workspace.activeVersionId
          : ((typeof workspace.validatedVersionId === 'string' && workspace.validatedVersionId.trim().length > 0)
              ? workspace.validatedVersionId
              : null);
      if (!activeVersionId) return false;

      const activeVersion = versions.find((version) => version.id === activeVersionId);
      if (!activeVersion?.parentId) return false;

      const parentVersion = versions.find((version) => version.id === activeVersion.parentId);
      if (!parentVersion) return false;

      const parentVisibility = Array.isArray(parentVersion.layerVisibility)
        ? parentVersion.layerVisibility
        : [];
      const parentLayerEntry = parentVisibility.find((entry) => entry.id === layerId);

      // Alt preview = only show layers that are visible in the parent version.
      return parentLayerEntry?.visible !== true;
    });
    const frameRectW = useKernelStore((state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect?.w);
    const frameRectH = useKernelStore((state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect?.h);
    const rectX = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.x);
    const rectY = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.y);
    const rectW = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.w);
    const rectH = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.rect.h);
    const scale = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.scale ?? 0);
    const opacity = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.opacity ?? 1);
    const locked = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.locked ?? 0);
    const layerVisibleFlag = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.visible !== false);
    const loadError = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.loadError ?? 0);
    const missingImageData = useKernelStore((state) => {
      const layer = state.inventory.StudioLayer.registry[layerId];
      if (isLayerVectorized(layer)) return false;
      if (getDrawingLayerPayload(layer?.params)) return false;
      return !layer?.imageData;
    });
    const imageDataWidth = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageData?.width);
    const imageDataHeight = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageData?.height);
    const layerParams = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.params);

    const currentLayerType = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.type ?? undefined);
    const renderAsSvg = useKernelStore((state) => {
      const layer = state.inventory.StudioLayer.registry[layerId];
      return isLayerVectorized(layer) || Boolean(getDrawingLayerPayload(layer?.params));
    });
    const layerTimelineStartMs = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.timelineStartMs);
    const layerTimelineEndMs = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.timelineEndMs);
    const workspaceTimelineEnabled = useKernelStore((state) => {
      const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
      if (!workspace) {
        return false;
      }
      const layerIds = Array.isArray(workspace.layerIds) ? workspace.layerIds : [];
      const hasTimelineEligibleVisibleLayer = layerIds.some((candidateLayerId) => {
        const layer = state.inventory.StudioLayer.registry[candidateLayerId];
        if (!layer || layer.visible === false) return false;
        if (layer.type === 'video' || layer.type === 'audio') {
          const source = typeof layer.videoSrc === 'string' ? layer.videoSrc.trim() : '';
          return source.length > 0;
        }
        // Raster/image layer
        const imageSrc = typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '';
        return imageSrc.length > 0 || Boolean(layer.imageData) || isLayerVectorized(layer) || Boolean(getDrawingLayerPayload(layer.params));
      });
      // NOTE: intentionally NOT gated on `preferVersionPanelOverTimeline`.
      // That flag only selects which side strip is shown (version thumbnails vs
      // the timeline track editor); it must not disable temporal compositing on
      // the canvas. Folding it in here meant that switching to the version view
      // ("outside the timeline display") dropped the cursor gate and stacked
      // every timeline layer on screen at once. Plain image artboards stay
      // unaffected: with no timeline duration/per-layer timing every layer
      // resolves to an infinite end and remains visible at the cursor.
      return hasTimelineEligibleVisibleLayer;
    });
    const workspaceTimelineCursorMs = useKernelStore((state) => {
      const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
      return Math.max(0, toFiniteTimelineMs(workspace?.timelineCursorMs) ?? 0);
    });
    const workspaceTimelineDurationMs = useKernelStore((state) => {
      const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
      return toFiniteTimelineMs(workspace?.timelineDurationMs);
    });
    const hasLoadError = Boolean(loadError);
    const isSuppressedForComposedPlayback =
      suppressForComposedPlayback &&
      (currentLayerType === 'video' || currentLayerType === 'audio');

    const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (isAnyNativeCutActive || isScissorsWaitingForTarget) {
            return;
        }
        handleLayerMouseDown && handleLayerMouseDown(event, layerId);
    }, [handleLayerMouseDown, layerId, isAnyNativeCutActive, isScissorsWaitingForTarget]);

    const onDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (activeTool !== null) return;
        if (onEditDrawingTextLayer?.(layerId) === true) {
            event.stopPropagation();
            event.preventDefault();
        }
    }, [activeTool, layerId, onEditDrawingTextLayer]);

    const isLayerVisibleAtTimelineCursor = useMemo(() => {
      if (!workspaceTimelineEnabled) return true;
      const startMs = Math.max(0, toFiniteTimelineMs(layerTimelineStartMs) ?? 0);
      const explicitEndMs = toFiniteTimelineMs(layerTimelineEndMs);
      const fallbackEndMs = currentLayerType === 'video' || currentLayerType === 'audio'
        ? Number.POSITIVE_INFINITY
        : (workspaceTimelineDurationMs !== null
            ? Math.max(startMs + 1, workspaceTimelineDurationMs)
            : Number.POSITIVE_INFINITY);
      const endMs = explicitEndMs !== null && explicitEndMs > startMs
        ? explicitEndMs
        : fallbackEndMs;
      return (
        workspaceTimelineCursorMs >= startMs &&
        workspaceTimelineCursorMs < endMs
      );
    }, [
      currentLayerType,
      layerTimelineEndMs,
      layerTimelineStartMs,
      workspaceTimelineCursorMs,
      workspaceTimelineDurationMs,
      workspaceTimelineEnabled,
    ]);

    const isVisible = useMemo(() => {
      // Multi-layer timeline preview: every video layer active within its own time
      // range is shown (isLayerVisibleAtTimelineCursor already gates that), so
      // overlapping clips composite and crossfade instead of only the top video
      // rendering. The former top-video-only gate lived here and in the playback
      // controller (syncTimelineVideoElementsAtCursor); both were relaxed together.
      return (
        !isSuppressedForComposedPlayback &&
        layerVisibleFlag &&
        isWorkspaceVisible &&
        !shouldHideLayerForParentPreview &&
        isLayerVisibleAtTimelineCursor
      );
    }, [
      isLayerVisibleAtTimelineCursor,
      isSuppressedForComposedPlayback,
      isWorkspaceVisible,
      layerVisibleFlag,
      shouldHideLayerForParentPreview,
    ]);

    const cursorStyle = useMemo(() => {
        if (activeTool === 'maskPen') {
            return getMaskPenCursor(maskPenMode);
        }
        if (activeTool === 'scissors') {
            return scissorsCursor;
        }
        if (activeTool === 'canvasControl') {
            return generationAreaCursor;
        }
        if (activeTool === 'draw') {
            return 'crosshair';
        }
        if (activeTool && ['cutter', 'eraser', 'pencil'].includes(activeTool)) {
            return 'crosshair';
        }
        if (activeTool !== null) {
            return 'default';
        }
        // No active tool from here
        if (!locked) {
            return 'grab';
        }
        if (!isWorkspaceLocked) {
            return 'move';
        }
        return 'default';
    }, [activeTool, locked, isWorkspaceLocked, maskPenMode]);

    const fallbackWidth = rectW && rectW > 0 ? rectW : 128;
    const fallbackHeight = rectH && rectH > 0 ? rectH : 128;
    const resolvedScale = useMemo(() => resolveLayerWorldPerPixel(rectW, imageDataWidth, scale) ?? 1, [rectW, imageDataWidth, scale]);
    const layerTransform = useMemo(() => resolveStudioLayerTransform(layerParams), [layerParams]);

    const containerStyle = useMemo((): React.CSSProperties => {
      const width =
        typeof rectW === 'number' && rectW > 0
          ? rectW
          : (imageDataWidth ? imageDataWidth * resolvedScale : (hasLoadError ? fallbackWidth : 0));
      const height =
        typeof rectH === 'number' && rectH > 0
          ? rectH
          : (imageDataHeight ? imageDataHeight * resolvedScale : (hasLoadError ? fallbackHeight : 0));
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        overflow: 'visible',
        display: isVisible ? 'block' : 'none',
        cursor: cursorStyle,
        opacity,
        transform: `translate(${typeof rectX === 'number' && Number.isFinite(rectX) ? rectX : 0}px, ${typeof rectY === 'number' && Number.isFinite(rectY) ? rectY : 0}px)`,
        width,
        height,
        pointerEvents: 'auto',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      };
    }, [rectX, rectY, rectW, rectH, imageDataWidth, imageDataHeight, resolvedScale, isVisible, cursorStyle, opacity, hasLoadError, fallbackWidth, fallbackHeight]);

    const contentTransformStyle = useMemo((): React.CSSProperties => ({
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      transformOrigin: '50% 50%',
      transform: buildStudioLayerContentCssTransform(layerTransform),
      userSelect: 'none',
      WebkitUserSelect: 'none',
    }), [layerTransform]);

    const debugRenderSnapshotRef = useRef<Record<string, unknown> | null>(null);
    useEffect(() => {
      if ((globalThis as any).__DEBUG_LAYER_CANVAS_RERENDER__ !== true) {
        return;
      }
      const debugTargetLayerId = (globalThis as any).__DEBUG_LAYER_CANVAS_LAYER_ID__;
      if (typeof debugTargetLayerId === 'string' && debugTargetLayerId.trim().length > 0 && debugTargetLayerId !== layerId) {
        return;
      }

      const hasFiniteFrameRect =
        typeof frameRectW === 'number' &&
        typeof frameRectH === 'number';
      const frameRectSignature = hasFiniteFrameRect
        ? `${frameRectW}|${frameRectH}`
        : 'null';

      const snapshot: Record<string, unknown> = {
        layerId,
        workspaceId,
        isWorkspaceVisible,
        isWorkspaceLocked,
        handleLayerMouseDown,
        handleResizeHandleMouseDown,
        resizeHandlesPortalHostRef,
        showResolutionHeatmap,
        activeTool,
        isAnyNativeCutActive,
        isScissorsWaitingForTarget,
        isNativeCutTarget,
        shouldHideLayerForPeek,
        shouldHideLayerForParentPreview,
        frameRectSignature,
        rectX,
        rectY,
        rectW,
        rectH,
        scale,
        opacity,
        locked,
        layerVisibleFlag,
        loadError,
        missingImageData,
        imageDataWidth,
        imageDataHeight,
        currentLayerType,
        isVisible,
        cursorStyle,
      };

      const previous = debugRenderSnapshotRef.current;
      if (!previous) {
        console.warn('[debug][LayerCanvas] initial render', snapshot);
      } else {
        const changedKeys = Object.keys(snapshot).filter((key) => !Object.is(previous[key], snapshot[key]));
        if (changedKeys.length > 0) {
          const changed: Record<string, { previous: unknown; next: unknown }> = {};
          changedKeys.forEach((key) => {
            changed[key] = {
              previous: previous[key],
              next: snapshot[key],
            };
          });
          console.warn('[debug][LayerCanvas] rerender diff', {
            layerId,
            changedKeys,
            changed,
          });
        } else {
          console.warn('[debug][LayerCanvas] rerender without diff', { layerId });
        }
      }
      debugRenderSnapshotRef.current = snapshot;
    });

    const containerTitle = hasLoadError ? 'Asset missing or corrupted.' : undefined;

    const heatmapData = useMemo((): { hue: number; style: React.CSSProperties } | null => {
      if (!showResolutionHeatmap) return null;
      if (currentLayerType === 'audio' || renderAsSvg || missingImageData) return null;

      // Goal: 16 Megapixels (16,000,000 pixels) for the entire workspace frame.
      const frameW = typeof frameRectW === 'number' ? frameRectW : 1;
      const frameH = typeof frameRectH === 'number' ? frameRectH : 1;
      const workspaceArea = frameW * frameH;
      const targetPixels = 16000000; 

      // Calculate the scale required to reach 6MP from the current frame size
      const exportScale = Math.sqrt(targetPixels / workspaceArea);

      // The layer's source width is layer.imageData.width.
      // Its width in the 6MP export would be: (sourceWidth * layer.scale) * exportScale.
      // Quality is the ratio of source pixels to output pixels.
      const currentScale = resolvedScale;
      const effectiveScale = currentScale * exportScale;
      const quality = 1 / effectiveScale;
      
      const logQuality = Math.log2(quality);
      let hue = 120; // Default Green (100% quality)
      
      if (logQuality < 0) {
        // Quality < 1.0 (Upscaled/Blurry) -> Shift towards Yellow (60) then Red (0)
        // logQuality is negative. -1 (50% quality) -> 120 - 60 = 60.
        hue = Math.max(0, 120 + (logQuality * 60));
      } else {
        // Quality > 1.0 (Downscaled/Sharp) -> Shift towards Green-Blue (Cyan 180)
        // logQuality is positive. 1 (200% quality) -> 120 + 30 = 150.
        hue = Math.min(180, 120 + (logQuality * 30));
      }

      return {
        hue,
        style:{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textShadow: '0 1px 2px black',
          fontSize: '12px',
          fontWeight: 'bold',
          fontFamily: 'monospace'
      }
      };
    }, [showResolutionHeatmap, resolvedScale, currentLayerType, renderAsSvg, missingImageData, frameRectW, frameRectH]);

    // LayerFeedbackOverlays is React.memo'd: an element built inline in its props would be a new
    // reference on every LayerCanvas render (the timeline cursor alone re-renders this component
    // many times per second during playback) and would re-render the whole overlay subtree.
    const ghostContent = useMemo(() => (
      renderAsSvg
        ? <Layer2DSvg layerId={layerId} />
        : <Layer2DCanvas layerId={layerId} workspaceId={workspaceId} heatmapHue={null} preview={preview} ghostResolution />
    ), [preview, renderAsSvg, layerId, workspaceId]);

    if (!isLayerScopedToWorkspace) return null;
    if(!kernel.current){
        console.error("Kernel is null when trying to load layers.")
        return null;
    } 

    const canRenderResizeHandles =
      activeTool !== 'canvasControl' &&
      !isSuppressedForComposedPlayback &&
      !locked &&
      currentLayerType !== 'audio' && !missingImageData &&
      !isAnyNativeCutActive;

    const resizeHandlesPortal = resizeHandlesPortalHostRef.current && canRenderResizeHandles
      ? createPortal(
          <>
            <LayerSelectionFrame layerId={layerId} />
            {RESIZE_HANDLE_CORNERS.map((corner) => (
              <LayerResizeHandle
                key={`layer-resize-handle-${layerId}-${corner}`}
                layerId={layerId}
                handleResizeHandleMouseDown={handleResizeHandleMouseDown}
                corner={corner}
              />
            ))}
          </>,
          resizeHandlesPortalHostRef.current
        )
      : null;

    return (
      <>
        {currentLayerType === 'audio' ? null : (
          <div
            style={containerStyle}
            onMouseDown={onMouseDown}
            onDoubleClick={onDoubleClick}
            title={containerTitle}
          >
            <div style={contentTransformStyle}>
              <LayerFeedbackOverlays layerId={layerId} ghostContent={ghostContent} disableGhostContent={preview} />
              {heatmapData && (
                <div style={heatmapData.style}>
                </div>
              )}
              {!shouldHideLayerForPeek && (
                currentLayerType === 'video'
                  ? <Layer2DVideo layerId={layerId} />
                  : renderAsSvg
                    ? <Layer2DSvg layerId={layerId} />
                    : <Layer2DCanvas layerId={layerId} workspaceId={workspaceId} heatmapHue={heatmapData?.hue ?? null} preview={preview} />
              )}
              {isNativeCutTarget && (
                <NativeLayerCutOverlay
                  layerId={layerId}
                  workspaceId={workspaceId}
                />
              )}
            </div>
          </div>
        )}
        {resizeHandlesPortal}
      </>
    );
  });

LayerCanvas.displayName = 'LayerCanvas';

export default LayerCanvas;
